import type {NamePath} from 'antd/es/form/interface';

import type {LangKeyType} from '../../../../../provider/locale/translation/type';
import {
    ImportCustomReviewsErrorItemType,
    parseImportCustomReviewsApiError,
} from '../../../../../service/reviews/reviews-custom-reviews-import-helper';

import {ImportReviewsFormFieldNamesEnum} from './import-reviews-form-type';

const IMPORT_REVIEWS_MAX_DISPLAYED_ROW_ERRORS = 5;
const IMPORT_REVIEWS_ROW_ERROR_COMMENT_FIELD = 'comment';

type FormFieldErrorType = {
    name: NamePath;
    errors: Array<string>;
};

type GetLocalizedStringType = (stringKey: LangKeyType, valueMap?: Record<string, string>) => string;

type ImportReviewsFieldErrorDescriptorType = {
    getValue: (errorItem: ImportCustomReviewsErrorItemType) => string | Array<string> | undefined;
    name: ImportReviewsFormFieldNamesEnum;
};

const importReviewsFieldErrorDescriptors: Array<ImportReviewsFieldErrorDescriptorType> = [
    {
        getValue: (errorItem) => errorItem.file,
        name: ImportReviewsFormFieldNamesEnum.reviewsFile,
    },
    {
        getValue: (errorItem) => (errorItem.expectedColumns?.length ? errorItem.expectedColumns.join(', ') : ''),
        name: ImportReviewsFormFieldNamesEnum.reviewsFile,
    },
    {
        getValue: (errorItem) => errorItem.brandId,
        name: ImportReviewsFormFieldNamesEnum.brand,
    },
    {
        getValue: (errorItem) => errorItem.companyId,
        name: ImportReviewsFormFieldNamesEnum.company,
    },
    {
        getValue: (errorItem) => errorItem.tagIds,
        name: ImportReviewsFormFieldNamesEnum.tags,
    },
];

function normalizeFieldErrorMessages(value: string | Array<string> | undefined): Array<string> {
    if (!value) {
        return [];
    }

    return Array.isArray(value) ? value : [value];
}

function limitDisplayedErrorMessages(messages: Array<string>, maxCount: number): Array<string> {
    if (messages.length <= maxCount) {
        return messages;
    }

    return [...messages.slice(0, maxCount), '...'];
}

function mapFormFieldErrorItem(errorItem: ImportCustomReviewsErrorItemType): Array<FormFieldErrorType> {
    return importReviewsFieldErrorDescriptors.flatMap(({getValue, name}) => {
        const errors = normalizeFieldErrorMessages(getValue(errorItem));

        if (errors.length === 0) {
            return [];
        }

        return [{name, errors}];
    });
}

function formatRowErrorMessage(
    errorItem: ImportCustomReviewsErrorItemType,
    getLocalizedString: GetLocalizedStringType
): string | null {
    if (!errorItem.message) {
        return null;
    }

    const row = String(errorItem.row ?? '');
    const field = errorItem.field?.trim();

    if (!field || field === IMPORT_REVIEWS_ROW_ERROR_COMMENT_FIELD) {
        return getLocalizedString('REVIEW_IMPORT__ROW_ERROR', {
            row,
            message: errorItem.message,
        });
    }

    return getLocalizedString('REVIEW_IMPORT__ROW_ERROR_WITH_FIELD', {
        row,
        field,
        message: errorItem.message,
    });
}

function appendFieldErrors(
    fieldErrors: Array<FormFieldErrorType>,
    fieldName: NamePath,
    messages: Array<string>
): Array<FormFieldErrorType> {
    if (messages.length === 0) {
        return fieldErrors;
    }

    const fieldIndex = fieldErrors.findIndex(({name}) => name === fieldName);

    if (fieldIndex < 0) {
        return [...fieldErrors, {name: fieldName, errors: messages}];
    }

    return fieldErrors.map((fieldError, index) =>
        index === fieldIndex ? {...fieldError, errors: [...fieldError.errors, ...messages]} : fieldError
    );
}

export function getImportReviewsFormFieldErrors(
    error: unknown,
    getLocalizedString: GetLocalizedStringType
): Array<FormFieldErrorType> {
    const parsed = parseImportCustomReviewsApiError(error);

    if (!parsed?.errors?.length) {
        return [];
    }

    const formFieldErrors: Array<FormFieldErrorType> = [];
    const rowErrorMessages: Array<string> = [];

    parsed.errors.forEach((errorItem) => {
        const rowErrorMessage = formatRowErrorMessage(errorItem, getLocalizedString);

        if (rowErrorMessage) {
            rowErrorMessages.push(rowErrorMessage);
            return;
        }

        formFieldErrors.push(...mapFormFieldErrorItem(errorItem));
    });

    return appendFieldErrors(
        formFieldErrors,
        ImportReviewsFormFieldNamesEnum.reviewsFile,
        limitDisplayedErrorMessages(rowErrorMessages, IMPORT_REVIEWS_MAX_DISPLAYED_ROW_ERRORS)
    );
}

export function getImportReviewsSnackbarErrorMessage(error: unknown): string | null {
    const parsed = parseImportCustomReviewsApiError(error);

    if (parsed?.detail) {
        return parsed.detail;
    }

    if (parsed?.nonFieldErrors?.[0]) {
        return parsed.nonFieldErrors[0];
    }

    return null;
}
